import React, { useMemo, useState } from "react";
import {
  Paper,
  Typography,
  IconButton,
  TextField,
  Box,
  Tooltip,
  Chip,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RedoIcon from "@mui/icons-material/Redo";
import colors from "../../../Constants/colors";
import { useTranslation } from "react-i18next";
import { PageRange } from "../../../Models/evaluation";
import PageRangesModal from "./PageRangesModal";
import { AnnexDocument } from "../../../Core/useAnnexActions";

export interface CardProps {
  doc: AnnexDocument;
  active?: boolean;
  onEditDone?: (val: string) => void;
  onClick?: () => void;
  onAddPress?: () => void;
  onCancel?: () => void;
  editable?: boolean;
  onPageRangesChange?: (ranges: PageRange[]) => void;
}

const LABEL_SLOT_W = 24; // Supports two-digit subsection numbers (e.g. 20.10, 20.13)

export const CardDoc: React.FC<CardProps> = ({
  doc,
  active,
  onEditDone,
  onClick,
  onAddPress,
  onCancel,
  editable = false,
  onPageRangesChange,
}) => {
  const { t } = useTranslation("evaluationPage");

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(doc.name);
  const [showPageModal, setShowPageModal] = useState(false);

  const isPdf = useMemo(
    () =>
      doc.type !== "empty_section" &&
      doc.type !== "title" &&
      (doc.originalName ?? doc.name).toLowerCase().endsWith(".pdf"),
    [doc]
  );

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleEditDone = () => {
    setIsEditing(false);
    onEditDone?.(editedName);
  };

  const handleDoneClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleEditDone();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: 56,
        pl: 1.2,
        pr: 1.2,
        py: 1,
        display: "flex",
        alignItems: "center",
        border: `1px solid ${active ? colors.accent : colors.gray200}`,
        borderLeft: `4px solid ${active ? colors.accent : colors.primary_dark}`,
        borderRadius: 2,
        bgcolor: active ? colors.orange50 : colors.white,
        cursor: isEditing ? "default" : "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": !isEditing
          ? {
              borderColor: colors.accent,
              borderLeftColor: colors.accent,
              bgcolor: active ? colors.orange200 : colors.orange50,
            }
          : {},
      }}
      onClick={() => onClick && onClick()}
    >
      {doc.order ? (
        <Box
          sx={{
            flex: `0 0 ${LABEL_SLOT_W}px`,
            mr: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            fontWeight: 700,
            color: colors.accent,
            whiteSpace: "nowrap",
            userSelect: "none",
          }}
        >
          {doc.order}.
        </Box>
      ) : null}

      <InsertDriveFileIcon sx={{ color: colors.accent }} />

      <Box sx={{ flex: "1 1 auto", minWidth: 0, ml: 1 }}>
        {isEditing ? (
          <TextField
            autoFocus
            variant="standard"
            value={editedName}
            size="small"
            fullWidth
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleEditDone}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            sx={{
              "& .MuiInput-underline:before, & .MuiInput-underline:after": {
                borderBottomColor: colors.accent,
              },
            }}
          />
        ) : (
          <Tooltip title={doc.name} placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: colors.primary_dark,
              }}
            >
              {doc.name}
            </Typography>
          </Tooltip>
        )}

        {doc.originalName && doc.originalName !== doc.name && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontSize: "0.75rem",
              opacity: 0.8,
              lineHeight: 1,
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: colors.gray600,
            }}
          >
            {doc.originalName}
          </Typography>
        )}
      </Box>

      {!isEditing && onCancel && (
        <IconButton
          size="small"
          sx={{ mr: 0.4, "&:hover": { bgcolor: colors.red50 } }}
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
        >
          <ClearOutlinedIcon fontSize="small" sx={{ color: colors.red600 }} />
        </IconButton>
      )}

      <Box sx={{ display: "flex", alignItems: "center" }}>
        {editable &&
          (isEditing ? (
            <Tooltip title={t("evaluationPage.done")}>
              <IconButton
                size="small"
                onClick={handleDoneClick}
                sx={{ mr: 0.4, "&:hover": { bgcolor: colors.gray100 } }}
              >
                <CheckIcon fontSize="small" sx={{ color: colors.accent }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t("evaluationPage.editName")}>
              <IconButton
                size="small"
                onClick={handleEditClick}
                sx={{ mr: 0.4, "&:hover": { bgcolor: colors.gray100 } }}
              >
                <EditIcon fontSize="small" sx={{ color: colors.accent }} />
              </IconButton>
            </Tooltip>
          ))}

        {isPdf && editable && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setShowPageModal(true);
            }}
            sx={{ "&:hover": { bgcolor: colors.gray100 } }}
          >
            {doc.pageRanges?.length ? (
              <FilterAltIcon fontSize="small" sx={{ color: colors.accent }} />
            ) : (
              <FilterAltOutlinedIcon
                fontSize="small"
                sx={{ color: colors.accent }}
              />
            )}
          </IconButton>
        )}

        {isPdf && doc.pageRanges?.length && editable ? (
          <Tooltip
            title={doc.pageRanges
              .map(({ start_page, end_page }: PageRange) =>
                start_page === end_page
                  ? `${start_page}`
                  : `${start_page}–${end_page}`
              )
              .join(", ")}
            placement="top"
          >
            <Chip
              label={doc.pageRanges.length}
              size="small"
              sx={{
                height: 18,
                ml: 0.2,
                bgcolor: colors.accent,
                color: colors.white,
                fontSize: "0.7rem",
              }}
            />
          </Tooltip>
        ) : null}

        {onAddPress && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onAddPress();
            }}
            sx={{ ml: 0.5, "&:hover": { bgcolor: colors.gray100 } }}
          >
            <RedoIcon
              fontSize="small"
              sx={{ color: colors.green700, transform: "rotate(-45deg)" }}
            />
          </IconButton>
        )}
      </Box>

      {isPdf && editable && (
        <PageRangesModal
          open={showPageModal}
          pageCount={doc.number_of_pages ?? 1}
          initialRanges={doc.pageRanges ?? []}
          onSave={(ranges) => onPageRangesChange?.(ranges)}
          onClose={() => setShowPageModal(false)}
        />
      )}
    </Paper>
  );
};
